import type { UserProfile, ConversationOptions } from '@/types/user';
import { freshchatAPI } from '@/services/freshchat.api';

/**
 * FreshchatService - Manages Freshchat widget for nutritionist-specific chat.
 * 
 * ARCHITECTURE OVERVIEW:
 * This service is specifically for the nutrition onboarding flow where users
 * chat with their assigned nutritionist. It is SEPARATE from the general
 * support chat widget used on home/FAQ pages.
 * 
 * KEY CONCEPTS:
 * 1. externalId: Our internal user ID (from customer profile)
 * 2. restoreId: Freshchat's user identifier for conversation history
 * 3. Static State: Class uses static variables to persist across component remounts
 * 
 * CROSS-USER CONTAMINATION PROBLEM:
 * The main issue this service addresses is preventing old user's data from
 * leaking to new users when:
 * - User A logs out and User B logs in
 * - User navigates away and comes back
 * - Multiple users use the same browser
 * 
 * SOLUTION - AGGRESSIVE CLEANUP:
 * The destroy() method performs comprehensive cleanup:
 * 1. Destroys Freshchat widget instance
 * 2. Removes script tag from DOM
 * 3. Clears ALL static variables (especially initData)
 * 4. Deletes window.fcWidget reference
 * 
 * CLEANUP TRIGGERS:
 * Cleanup is triggered when:
 * 1. User logs out (via useAuthFlow logout function)
 * 2. User navigates away from nutrition page (via useFreshchat route detection)
 * 3. Component unmounts (via useEffect cleanup in UserOnboarding component)
 * 
 * INITIALIZATION FLOW:
 * 1. Fetch customer profile (externalId + restoreId)
 * 2. Load Freshchat SDK script
 * 3. Initialize widget with user data
 * 4. Set callback to save new restoreId if user doesn't have one
 * 5. Widget ready for use
 * 
 * IMPORTANT NOTES:
 * - This widget is ONLY for nutrition onboarding page
 * - General support chat on home/FAQ pages is separate
 * - Each user session must start with fresh initialization
 * - Static state is necessary for SDK but must be cleared on cleanup
 */

interface FreshchatInitData {
  externalId?: string;
  restoreId?: string;
}

export class FreshchatService {
  private static isNutritionistChatInitialized = false;
  private static initializationRetry = 0;
  private static maxRetries = 3;
  private static restoreIdCallback: ((restoreId: string) => Promise<void>) | null = null;
  private static widgetLoadedPromise: Promise<void> | null = null;
  private static widgetLoadedResolver: (() => void) | null = null;
  private static initData: FreshchatInitData | null = null;
  
  static async loadAndInit(userData?: FreshchatInitData): Promise<void> {
    if (this.isNutritionistChatInitialized) return;
    console.log('999 :: 87676 :: inside loadAndInit ............... this.isNutritionistChatInitialized ::  ', this.isNutritionistChatInitialized);
    
    // Store user data for initialization
    if (userData) {
      this.initData = userData;
    }
    
    const scriptExists = document.getElementById('freshchat-nutritionist-sdk');
    
    if (!scriptExists) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = 'freshchat-nutritionist-sdk';
        script.async = true;
        script.src = 'https://wchat.freshchat.com/js/widget.js';
        
        script.onload = () => {
          setTimeout(() => {
            if (window.fcWidget) {
              console.log('999 :: 87676 :: inside loadAndInit >>>>> script.onload ...............  resolved ');
              this.initWidget();
              resolve();
            } else {
              console.log('999 :: 87676 :: inside loadAndInit >>>>> script.onload ............... rejected ');              
              reject(new Error('FreshChat widget not available'));
            }
          }, 500);
        };

        script.onerror = () => reject(new Error('Failed to load FreshChat SDK'));
        document.head.appendChild(script);
      });
    } else {
      // await new Promise(resolve => setTimeout(resolve, 500));  // TODO
      if (!this.isNutritionistChatInitialized) {
        this.initWidget();
      }
    }
  }
  
  private static initWidget(): void {
    if (this.isNutritionistChatInitialized) return;

    if (!window.fcWidget) {
      this.initializationRetry++;
      if (this.initializationRetry > this.maxRetries) {
        throw new Error('Failed to initialize FreshChat widget');
      } 
      this.loadAndInit();
      return;
    }

    // Create a promise that resolves when widget is loaded
    this.widgetLoadedPromise = new Promise<void>((resolve) => {
      this.widgetLoadedResolver = resolve;
    });

    console.log('999 :: 87676 :: inside initWidget >>>>> start ............... ');
    console.log('999 :: 345546 :: window.fcWidget :: ', window.fcWidget);
    console.log('999 :: 345546 :: initData :: ', this.initData);

    // Build init config with externalId and restoreId if available
    const initConfig: any = {
      token: process.env.NEXT_PUBLIC_FRESHCHAT_TOKEN || '',
      host: process.env.NEXT_PUBLIC_FRESHCHAT_HOST || '',
      widgetUuid: process.env.NEXT_PUBLIC_WIDGET_UUID || '',
      config: {
        headerProperty: {
          hideChatButton: true
        }
      }
    };

    // Add externalId and restoreId to init if available
    if (this.initData?.externalId) {
      initConfig.externalId = this.initData.externalId;
      console.log('999 :: 345546 :: Setting externalId during init:', this.initData.externalId);
    }
    
    if (this.initData?.restoreId) {
      initConfig.restoreId = this.initData.restoreId;
      console.log('999 :: 345546 :: Setting restoreId during init:', this.initData.restoreId);
    }

    console.log('999 :: 345546 :: initConfig:', initConfig);

    window.fcWidget.init(initConfig);

    window.fcWidget.on("widget:loaded", () => {
      console.log('Freshchat widget loaded');
      this.isNutritionistChatInitialized = true;
      // Resolve the promise when widget is loaded
      if (this.widgetLoadedResolver) {
        this.widgetLoadedResolver();
      }
    });

    window.fcWidget.on('user:created', async (resp) => {
      const restoreId = resp?.data?.restoreId;
      console.log('999 :: 87676 :: inside initWidget >>>>> window.fcWidget.on ::: resp >> ', resp, this.restoreIdCallback);
      if (restoreId && this.restoreIdCallback) {
        await this.restoreIdCallback(restoreId);
      }
    });
  }
  
  static setRestoreIdCallback(callback: ((restoreId: string) => Promise<void>) | null): void {
    this.restoreIdCallback = callback;
  }
  
  static async identifyAndSetUser(userData: UserProfile): Promise<void> {
    if (!window.fcWidget) return;
    
    // Wait for widget to be fully loaded before identifying user
    if (this.widgetLoadedPromise) {
      console.log('999 :: 34335 :: Waiting for widget to load before identifying user...');
      await this.widgetLoadedPromise;
      console.log('999 :: 34335 :: Widget loaded, proceeding with user identification');
    }
    
    console.log('999 :: 34335 :: inside identifyAndSetUser :::::::::::::: ', userData);
    
    const { id, name, email, primary_contact, country_code, freshchat_restore_id } = userData;
    
    const [firstName, ...lastNameParts] = (name || '').split(' ');
    const lastName = lastNameParts.join(' ');
    const phone = primary_contact?.slice(country_code?.length || 0);
    
    // Note: externalId and restoreId are now set during init(), not here
    console.log('999 :: 34335 :: externalId and restoreId already set during init');
    console.log('999 :: 34335 :: Setting additional user properties...');

    console.log('999 :: 878676565455 :: flow continues identifyAndSetUser : freshchat_restore_id :: ', freshchat_restore_id);
    
    window.fcWidget.user.setProperties({
      externalId: id.toString(),
      restoreId: freshchat_restore_id,
      firstName: firstName || '',
      lastName: lastName || '',
      email: email || '',
      phone: phone || '',
      phoneCountryCode: country_code || ''
    });
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  static async openConversation(options: ConversationOptions): Promise<void> {
    if (!window.fcWidget) return;

    console.log('999 :: 678676 :: inside openConversation function call :: options :: ', options);
    
    window.fcWidget.user.setProperties({
      talk_to: options.talk_to,
      nutritionist_id: options.nutritionist_id,
      customer_id: options.customer_id
    });

     window.fcWidget.setTags(["talk_to_nutritionist"]);

    console.log('999 :: 8765678 :: inside openConversation function call :: window.fcWidget.user :: ', window.fcWidget.user);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    window.fcWidget.open();
  }
  
  /**
   * Completely destroys the Freshchat widget and resets all state.
   * This is an aggressive cleanup that ensures no data persists across user sessions.
   * 
   * Called when:
   * - User logs out
   * - User navigates away from nutrition onboarding page
   * - Component unmounts
   * 
   * Why aggressive cleanup:
   * - Prevents cross-contamination between different user sessions
   * - Ensures old user's restoreId/externalId don't leak to new user
   * - Clears all static state that persists across component remounts
   */
  static destroy(): void {
    console.log('999 :: CLEANUP :: Starting aggressive Freshchat cleanup');
    
    // Step 1: Destroy the Freshchat widget instance if it exists
    if (window.fcWidget) {
      try {
        console.log('999 :: CLEANUP :: Destroying fcWidget instance');
        window.fcWidget.destroy();
        
        // Explicitly delete the window reference to prevent any lingering references
        delete (window as any).fcWidget;
        console.log('999 :: CLEANUP :: fcWidget reference deleted from window');
      } catch (error) {
        console.error('999 :: CLEANUP :: Error destroying fcWidget:', error);
      }
    }
    
    // Step 2: Remove the Freshchat script tag from DOM
    // This ensures a fresh script load on next initialization
    const scriptElement = document.getElementById('freshchat-nutritionist-sdk');
    if (scriptElement) {
      console.log('999 :: CLEANUP :: Removing Freshchat script tag from DOM');
      scriptElement.remove();
    }
    
    // Step 3: Reset ALL static variables to initial state
    // CRITICAL: This prevents old user data from contaminating new user sessions
    console.log('999 :: CLEANUP :: Resetting all static state variables');
    
    this.isNutritionistChatInitialized = false;
    this.initializationRetry = 0;
    this.restoreIdCallback = null;
    this.widgetLoadedPromise = null;
    this.widgetLoadedResolver = null;
    
    // CRITICAL: Clear initData which stores externalId and restoreId
    // This was the main source of cross-contamination between users
    this.initData = null;
    
    console.log('999 :: CLEANUP :: All static state cleared');
    console.log('999 :: CLEANUP :: Aggressive cleanup completed successfully');
  }
}
